import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTrialContext } from '../context/TrialContext';
import { initPostHog, trackHomepageVisit } from '../services/analytics';

// Landing page components
import {
  Hero,
  ChoosePath,
  HowItWorksStudents,
  HowItWorksTeachers,
  QuickChallenge,
  PracticeMistakes,
  ExamPresets,
  BenefitsGrid,
  ToolsOverview,
  FinalCTA,
  LandingFooter,
  LandingNavbar,
  TrialModal,
} from '../components/landing';

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { trialEmail, isTrialActive, startTrial } = useTrialContext();
  
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [audienceMode, setAudienceMode] = useState(null); // 'student' or 'teacher'
  const analyticsInitRef = React.useRef(false);

  // Initialize analytics on mount (with ref guard to prevent duplicate)
  useEffect(() => {
    if (analyticsInitRef.current) return;
    analyticsInitRef.current = true;
    initPostHog();
    trackHomepageVisit();
  }, []);

  const handleStartTrial = async (e) => {
    e.preventDefault();
    setError('');
    setModalLoading(true);

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      setModalLoading(false);
      return;
    }

    const result = await startTrial(email);
    
    if (result.success) {
      setShowTrialModal(false);
      navigate('/quiz/generate');
    } else {
      setError(result.error || 'Failed to start trial');
    }
    setModalLoading(false);
  };

  const handleGetStarted = (mode) => {
    setAudienceMode(mode);
    if (user) {
      navigate('/dashboard');
    } else if (isTrialActive) {
      navigate('/quiz/generate');
    } else {
      setShowTrialModal(true);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const openTrialModal = () => {
    setAudienceMode('student');
    setShowTrialModal(true);
  };

  return (
    <div className={`min-h-screen font-['Plus_Jakarta_Sans'] ${isDarkMode ? 'bg-[#0B0F17] text-slate-100' : 'bg-[#F8FAFC] text-slate-900'} transition-colors duration-300`}>
      
      {/* Navigation */}
      <LandingNavbar
        isDarkMode={isDarkMode}
        toggleDarkMode={toggleDarkMode}
        user={user}
        onScrollToSection={scrollToSection}
        onOpenTrialModal={openTrialModal}
      />

      {/* Trial Modal */}
      {showTrialModal && (
        <TrialModal
          isDarkMode={isDarkMode}
          audienceMode={audienceMode}
          email={email}
          setEmail={setEmail}
          error={error}
          modalLoading={modalLoading}
          onSubmit={handleStartTrial}
          onClose={() => setShowTrialModal(false)}
        />
      )}

      {/* Hero Section */}
      <Hero
        isDarkMode={isDarkMode}
        isTrialActive={isTrialActive}
        trialEmail={trialEmail}
        user={user}
        onGetStarted={handleGetStarted}
      />

      {/* Choose Your Path Section */}
      <ChoosePath
        isDarkMode={isDarkMode}
        onGetStarted={handleGetStarted}
        onScrollToSection={scrollToSection}
      />

      {/* How It Works - Students */}
      <HowItWorksStudents
        isDarkMode={isDarkMode}
        onGetStarted={handleGetStarted}
      />

      {/* How It Works - Teachers */}
      <HowItWorksTeachers
        isDarkMode={isDarkMode}
        onGetStarted={handleGetStarted}
      />

      {/* Quick Study Challenge */}
      <QuickChallenge
        isDarkMode={isDarkMode}
        onGetStarted={handleGetStarted}
      />

      {/* Practice Mistakes Feature */}
      <PracticeMistakes isDarkMode={isDarkMode} />

      {/* Exam Presets */}
      <ExamPresets
        isDarkMode={isDarkMode}
        onGetStarted={handleGetStarted}
      />

      {/* Benefits Dual Grid */}
      <BenefitsGrid isDarkMode={isDarkMode} />

      {/* Tools Overview */}
      <ToolsOverview isDarkMode={isDarkMode} />

      {/* Final CTA */}
      <FinalCTA onGetStarted={handleGetStarted} />

      {/* Footer */}
      <LandingFooter
        isDarkMode={isDarkMode}
        onScrollToSection={scrollToSection}
      />
    </div>
  );
};

export default Landing;
